import { EquipmentItem, Spell, Modifier } from '../types/character';
import { createEquipmentInstanceWithUniqueModifiers, generateUniqueEquipmentId } from './modifierHelpers';

// Helper function to find domain card by ID
export const findDomainCard = async (itemId: string): Promise<any> => {
  const domainCardsData = await import('../data/domain-cards.json');
  return domainCardsData.domainCards.find((card: any) => card.id === itemId);
};

// Helper function to transform domain card to spell format
// NOTE: This function is maintained for backward compatibility during Phase 44 transition
// Domain cards now contain structured formula objects in the database
export const transformDomainCardToSpell = (domainCard: any): Spell => {
  // Map domain card types to character spell types
  const spellType = domainCard.type === 'spell' ? 'active' : 
                   domainCard.type === 'ability' ? 'passive' : 
                   'passive'; // Default to passive for other types

  // Domain card modifiers are now structured objects from the database migration
  // No need for legacy formula parsing - just pass through the existing modifiers
  const modifiers = domainCard.modifiers || [];

  return {
    id: domainCard.id,
    name: domainCard.name,
    type: spellType as 'passive' | 'active' | 'concentration',
    description: domainCard.description || domainCard.content,
    modifiers,
    domain: domainCard.domain  // Preserve domain for condition evaluation
  };
};

// Helper function to find equipment by ID and category
// Also searches loot and consumables (all equippable items)
export const findEquipment = async (itemId: string, category?: string): Promise<any> => {
  // Load all equipment files (including loot and consumables)
  const [weaponsData, armorData, wheelchairData, lootData, consumablesData] = await Promise.all([
    import('../data/equipment/weapons.json'),
    import('../data/equipment/armor.json'),
    import('../data/equipment/wheelchairs.json'),
    import('../data/equipment/loot.json'),
    import('../data/equipment/consumables.json'),
  ]);

  // Search in all categories
  const allItems = [
    ...Object.values(weaponsData.primaryWeapons).flat(),
    ...Object.values(weaponsData.secondaryWeapons || {}).flat(),
    ...Object.values(armorData.armor).flat(),
    ...Object.values(wheelchairData.wheelchairs).flat(),
    ...(lootData.loot || []),
    ...(consumablesData.consumables || []),
  ];

  return allItems.find((item: any) => item.id === itemId);
};

// Helper function to transform equipment data to character format with unique modifier IDs
export const transformEquipmentToCharacter = (equipmentData: any, uniqueId: string): EquipmentItem => {
  // Use the new helper that handles unique modifier IDs
  return createEquipmentInstanceWithUniqueModifiers(equipmentData, uniqueId);
};

// Re-export the generateUniqueEquipmentId function for convenience
export { generateUniqueEquipmentId };

// Helper function to find loot by ID
export const findLoot = async (itemId: string): Promise<any> => {
  const lootData = await import('../data/equipment/loot.json');
  return lootData.loot.find((item: any) => item.id === itemId);
};

// Helper function to find consumable by ID
export const findConsumable = async (itemId: string): Promise<any> => {
  const consumablesData = await import('../data/equipment/consumables.json');
  return consumablesData.consumables.find((item: any) => item.id === itemId);
};